import { useEffect } from "react";
import { Link } from "react-router-dom";
import FadeInSection from "@/components/FadeInSection";
import ArticleTagBadge from "@/components/ArticleTagBadge";
import { getCategoryColor } from "@/lib/categoryColors";
import { getArticleUrl } from "@/lib/articleUrls";
import { getAuthor } from "@/data/journalists";
import { Skeleton } from "@/components/ui/skeleton";
import type { Article } from "@/components/NewsSection";
import type { LiveMarket } from "@/hooks/useLiveMarkets";
import { SectionLabel } from "./SectionLabel";
import { StoryRow } from "./StoryComponents";
import { LiveMarketCard } from "./LiveMarketCard";
import { timeAgo } from "./timeAgo";

/** Inject a <link rel="preload"> for the hero (LCP) image as early as possible */
function usePreloadImage(src: string | undefined) {
  useEffect(() => {
    if (!src) return;
    const id = "__lcp-preload";
    if (document.getElementById(id)) {
      (document.getElementById(id) as HTMLLinkElement).href = src;
      return;
    }
    const link = document.createElement("link");
    link.id = id;
    link.rel = "preload";
    link.as = "image";
    link.href = src;
    link.fetchPriority = "high";
    document.head.appendChild(link);
    return () => { link.remove(); };
  }, [src]);
}

export const HeroSection = ({
  lead,
  topStories,
  markets,
  marketsLoading,
  articlesSource,
  articlesLoading = false,
}: {
  lead: Article | undefined;
  topStories: Article[];
  markets: LiveMarket[];
  marketsLoading: boolean;
  articlesSource: Article[];
  articlesLoading?: boolean;
}) => {
  usePreloadImage(lead?.image);

  return (
  <div className="max-w-[1440px] mx-auto px-4 md:px-8 pt-6 min-h-[500px] md:min-h-[780px]">
    <div className="grid min-w-0 gap-8 lg:grid-cols-12">
      <div className="min-w-0 lg:col-span-8 flex flex-col">
        <SectionLabel label="Top Stories" href="/news">
          <span className="text-[10px] text-muted-foreground font-medium">{articlesSource.length} articles</span>
        </SectionLabel>
        {articlesLoading && !lead && (
          <div className="space-y-4 animate-pulse">
            <Skeleton className="aspect-[16/9] w-full rounded-lg" />
            <Skeleton className="h-6 w-3/4" />
            <Skeleton className="h-4 w-1/2" />
            <div className="space-y-3 mt-6">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="flex gap-3">
                  <Skeleton className="w-20 h-14 rounded-md shrink-0" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-3 w-2/3" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        {lead && (
          <>
            {/* Mobile layout */}
            <div className="min-w-0 overflow-hidden sm:hidden">
              <FadeInSection>
                <Link to={getArticleUrl(lead)} className="group block min-w-0">
                  <div className="aspect-[16/9] rounded-lg bg-secondary overflow-hidden mb-2 relative">
                    <img
                      src={lead.image}
                      srcSet={lead.image?.includes('sanity') ? `${lead.image}&w=480 480w, ${lead.image}&w=768 768w, ${lead.image}&w=960 960w` : undefined}
                      sizes={lead.image?.includes('sanity') ? "(max-width: 640px) 100vw, 60vw" : undefined}
                      alt={lead.title}
                      className="w-full h-full object-cover object-center opacity-80 group-hover:opacity-100 transition-opacity"
                      fetchPriority="high"
                      width={960}
                      height={540}
                    />
                    {lead.tag && (
                      <div className="absolute top-2 left-2">
                        <ArticleTagBadge tag={lead.tag} />
                      </div>
                    )}
                  </div>
                  <span
                    className="text-[10px] font-semibold tracking-wider uppercase"
                    style={{ color: getCategoryColor(lead.category).hex }}
                  >
                    {lead.category}
                  </span>
                  <h2 className="text-lg font-extrabold text-foreground mt-1 leading-snug hover-highlight transition-colors line-clamp-3">
                    {lead.title}
                  </h2>
                  <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{lead.excerpt}</p>
                  {(() => {
                    const author = getAuthor(lead.slug, lead.author);
                    return author ? (
                      <div className="flex min-w-0 items-center gap-2 mt-2 text-[11px] text-muted-foreground">
                        <img src={author.avatar} alt={author.name} className="w-5 h-5 rounded-full bg-secondary shrink-0" />
                        <span className="min-w-0 truncate font-medium text-foreground">{author.name}</span>
                        <span className="shrink-0">·</span>
                        <span className="shrink-0">{timeAgo(lead.date)}</span>
                      </div>
                    ) : null;
                  })()}
                </Link>
              </FadeInSection>
              <FadeInSection delay={0.05}>
                <div className="divide-y divide-border/50 mt-3">
                  {topStories.map((a) => (
                    <StoryRow key={a.id} article={a} />
                  ))}
                </div>
              </FadeInSection>
            </div>

            {/* Desktop layout */}
            <div className="hidden sm:grid grid-cols-12 gap-5 flex-1">
              <div className="col-span-7 min-w-0 h-full">
                <FadeInSection className="h-full">
                  <Link to={getArticleUrl(lead)} className="group flex flex-col h-full">
                    <div className="aspect-[4/3] rounded-lg bg-secondary overflow-hidden mb-3 relative shrink-0">
                      <img
                        src={lead.image}
                        srcSet={lead.image?.includes('sanity') ? `${lead.image}&w=480 480w, ${lead.image}&w=768 768w, ${lead.image}&w=960 960w` : undefined}
                        sizes={lead.image?.includes('sanity') ? "(max-width: 768px) 100vw, 60vw" : undefined}
                        alt={lead.title}
                        className="w-full h-full object-cover object-center opacity-80 group-hover:opacity-100 transition-opacity"
                        fetchPriority="high"
                        width={960}
                        height={540}
                      />
                      {lead.tag && (
                        <div className="absolute top-3 left-3">
                          <ArticleTagBadge tag={lead.tag} />
                        </div>
                      )}
                    </div>
                    <span
                      className="text-[10px] font-semibold tracking-wider uppercase shrink-0"
                      style={{ color: getCategoryColor(lead.category).hex }}
                    >
                      {lead.category}
                    </span>
                    <h2 className="text-lg md:text-xl font-extrabold text-foreground mt-1 leading-tight hover-highlight transition-colors line-clamp-3 shrink-0">
                      {lead.title}
                    </h2>
                    <p className="text-base text-muted-foreground mt-2 leading-relaxed line-clamp-2">{lead.excerpt}</p>
                    {(() => {
                      const author = getAuthor(lead.slug, lead.author);
                      return author ? (
                        <div className="flex items-center gap-2 mt-auto text-[11px] text-muted-foreground shrink-0 pb-2">
                          <img src={author.avatar} alt={author.name} className="w-5 h-5 rounded-full bg-secondary" />
                          <span className="font-medium text-foreground">{author.name}</span>
                          <span>·</span>
                          <span>{timeAgo(lead.date)}</span>
                        </div>
                      ) : null;
                    })()}
                  </Link>
                </FadeInSection>
              </div>
              <div className="col-span-5 min-w-0 h-full">
                <FadeInSection delay={0.05} className="h-full">
                  <div className="divide-y divide-border/50 flex flex-col justify-between h-full min-h-[480px]">
                    {topStories.map((a) => (
                      <StoryRow key={a.id} article={a} className="py-3 flex-1 flex items-center" />
                    ))}
                  </div>
                </FadeInSection>
              </div>
            </div>
          </>
        )}
      </div>
      <div className="min-w-0 lg:col-span-4 flex flex-col">
        <FadeInSection delay={0.1} className="h-full flex flex-col">
          <SectionLabel label="Market Pulse" href="/markets">
            <span className="flex items-center gap-1.5 text-[10px] text-muted-foreground">
              <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
              Live
            </span>
          </SectionLabel>
          <div className="flex min-w-0 max-w-full gap-2.5 overflow-x-auto pb-2 scrollbar-hide snap-x snap-mandatory lg:flex-col lg:overflow-visible lg:pb-0 lg:gap-3 lg:min-h-[480px] lg:h-auto lg:justify-between flex-1">
            {marketsLoading
              ? Array.from({ length: 5 }).map((_, i) => (
                  <div
                    key={i}
                    className="snap-start shrink-0 w-[75vw] sm:w-[260px] lg:w-auto rounded-lg border border-border bg-card p-3 space-y-2 flex-1 flex flex-col justify-center"
                  >
                    <Skeleton className="h-4 w-3/4" />
                    <Skeleton className="h-6 w-1/4" />
                  </div>
                ))
              : markets.slice(0, 5).map((m) => (
                  <div key={m.id} className="snap-start shrink-0 w-[75vw] sm:w-[260px] lg:w-auto flex-1 flex flex-col">
                    <div className="h-full flex-1 [&>div]:h-full">
                      <LiveMarketCard market={m} />
                    </div>
                  </div>
                ))}
          </div>
        </FadeInSection>
      </div>
    </div>
  </div>
  );
};
